import { MetricDetails } from '../../../../api/metrics/types';
import { SQLFilters } from '../../../../api/types';
import { MetricId } from '../../../../api/types-graphql';
import { MetricIdType, SingleValueTimeSelection } from '../../../../types';
import { LanguageString } from '../../dashboards/types';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import { ExternalChartComponentProps } from '../common/types';

/**
 * Configuration for a single row in the metric table
 */
export interface MetricTableRowConfig {
  /** Label for the row (displayed in first column) */
  label: LanguageString;
  /** Metrics for each cell in the row (null = empty cell) */
  cells: (MetricIdType | null)[];
}

/**
 * Configuration for the metric table visualization
 */
export interface MetricTableVizConfig {
  /** Optional title for the table */
  title?: LanguageString;
  /** Headers for each column (excluding the row label column) */
  headers: LanguageString[];
  /** Row configurations */
  rows: MetricTableRowConfig[];
  /** Time selection for all metric queries */
  timeSelection: SingleValueTimeSelection;
  /** Optional filters to apply to all queries */
  filters?: SQLFilters;
}

/**
 * A single metric value result (raw, unformatted)
 */
export interface MetricTableCellValue {
  metric: MetricIdType | null;
  /** Raw numeric value, null if metric is null or data unavailable */
  value: number | null;
  metricSql: string | null;
}

/**
 * Data response for a single row
 */
export interface MetricTableRowData {
  label: string;
  cells: MetricTableCellValue[];
}

/**
 * Data response for the metric table
 */
export interface MetricTableVizDataResponse {
  rows: MetricTableRowData[];
}

/**
 * Full data object for the metric table visualization
 */
export interface MetricTableVizData {
  dataResponse: MetricTableVizDataResponse | null;
  titleConfig: TitleConfig;
  headers: string[];
  metricDetails: Partial<Record<MetricId, MetricDetails>>;
}

/**
 * External props for the metric table visualization
 */
export type MetricTableVizExternalProps = ExternalChartComponentProps;

/**
 * Available views for the metric table (currently only table)
 */
export enum MetricTableVizViews {
  TABLE = 'table',
}
